import React, { useState, useMemo } from 'react';
import { Modal, Field, Input, Btn } from '../components/UI';
import { calcCaja, ventasHoy, ventasMes, promedioDiario, calcFlujo, fmtM, fmtDate, todayStr, COLORS_SOCIOS, ini } from '../utils';
import { showToast } from '../components/UI';

export default function DashboardSocio({ store, setItem, currentUser }) {
  const { movimientos, compras, costosMes, socios } = store;
  const [ventaModal, setVentaModal] = useState(false);
  const [ventaForm, setVentaForm] = useState({});
  const [retiroModal, setRetiroModal] = useState(false);
  const [retiroForm, setRetiroForm] = useState({});
  const [simMonto, setSimMonto] = useState('');

  const hoy = todayStr();
  const caja = useMemo(() => calcCaja(movimientos), [movimientos]);
  const hoyVentas = useMemo(() => ventasHoy(movimientos), [movimientos]);
  const mesVentas = useMemo(() => ventasMes(movimientos), [movimientos]);
  const prom20 = useMemo(() => promedioDiario(movimientos, 20), [movimientos]);
  const ajuste = parseFloat(localStorage.getItem('flujo-ajuste') || '0');
  const flujo = useMemo(() => calcFlujo(movimientos, compras, costosMes, ajuste), [movimientos, compras, costosMes, ajuste]);

  // This socio's account
  const nombre = currentUser?.name || '';
  const socioData = Object.values(socios || {}).find(s => s.nombre === nombre) || { saldo: 0, movimientos: {} };
  const totalRetiros = Object.values(movimientos || {}).filter(m => m.tipo === 'retiro' && m.socio === nombre).reduce((a, m) => a + (parseFloat(m.monto) || 0), 0);
  const disponible = (parseFloat(socioData.saldo) || 0) - totalRetiros;

  // Simulador
  const simulacion = useMemo(() => {
    const monto = parseFloat(simMonto) || 0;
    if (!monto) return null;
    const p = flujo;
    const balanceConCompra = p.totalProyectado - p.pagosEnPeriodo - monto;
    const necesario = p.pagosEnPeriodo + monto - p.caja;
    const ingDiarioNec = p.diasRestantes > 0 ? necesario / p.diasRestantes : 0;
    const promAj = Math.max(0, prom20 - ajuste);
    return {
      monto, balanceConCompra,
      viable: balanceConCompra >= 0,
      ingDiarioNec, margen: promAj - ingDiarioNec,
      vencimiento: flujo.bounds?.hastaConJueves,
    };
  }, [simMonto, flujo, prom20, ajuste]);

  function openVenta() {
    const hora = new Date().toTimeString().slice(0, 5);
    setVentaForm({ fecha: hoy, hora, ef: '', tr: '', qr: '', turno: 'cierre_dia' });
    setVentaModal(true);
  }

  function saveVenta() {
    const ef = parseFloat(ventaForm.ef) || 0;
    const tr = parseFloat(ventaForm.tr) || 0;
    const qr = parseFloat(ventaForm.qr) || 0;
    const total = ef + tr + qr;
    if (!total) { showToast('Ingresá al menos un monto'); return; }
    const id = 'm' + Date.now();
    const turnoLabels = { cierre_dia: 'Cierre del día', cierre_manana: 'Cierre mañana', cierre_tarde: 'Cierre tarde', venta_externa: 'Venta externa', otro: 'Otro' };
    setItem('movimientos', id, {
      id, tipo: 'venta', fecha: ventaForm.fecha, hora: ventaForm.hora,
      monto: total, monto_efectivo: ef, monto_transferencia: tr, monto_qr: qr,
      medio: ef > 0 && tr === 0 && qr === 0 ? 'efectivo' : 'mixto',
      signo: '+', turno: ventaForm.turno,
      desc: turnoLabels[ventaForm.turno],
      autor: nombre
    });
    showToast('Venta registrada');
    setVentaModal(false);
  }

  function openRetiro() {
    setRetiroForm({ monto: '', medio: 'efectivo', fecha: hoy, obs: '' });
    setRetiroModal(true);
  }

  function saveRetiro() {
    const monto = parseFloat(retiroForm.monto);
    if (!monto || monto <= 0) { showToast('Ingresá un monto'); return; }
    const id = 'm' + Date.now();
    setItem('movimientos', id, {
      id, tipo: 'retiro', socio: nombre, fecha: retiroForm.fecha,
      hora: new Date().toTimeString().slice(0, 5),
      monto, medio: retiroForm.medio, signo: '-',
      desc: `Retiro ${nombre}${retiroForm.obs ? ' — ' + retiroForm.obs : ''}`,
      autor: nombre
    });
    showToast(`Retiro de ${fmtM(monto)} registrado`);
    setRetiroModal(false);
  }

  const totalVenta = (parseFloat(ventaForm.ef)||0) + (parseFloat(ventaForm.tr)||0) + (parseFloat(ventaForm.qr)||0);

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Greeting */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>Hola, {nombre} 👋</div>
        <div style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 2 }}>{fmtDate(hoy)}</div>
      </div>

      {/* Quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Ventas hoy', val: fmtM(hoyVentas), color: 'var(--grn)', icon: '📈' },
          { label: 'Ventas del mes', val: fmtM(mesVentas), color: 'var(--blu-m)', icon: '📅' },
          { label: 'Caja total', val: fmtM(caja.total), color: caja.total >= 0 ? 'var(--grn)' : 'var(--red)', icon: '💰' },
          { label: 'Flujo al jueves', val: fmtM(flujo.solvencia), color: flujo.solvencia >= 0 ? 'var(--grn)' : 'var(--red)', icon: flujo.solvencia >= 0 ? '✅' : '⚠️' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: '16px' }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Courier, monospace', color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Mi cuenta */}
      <div style={{ background: 'var(--surf)', border: `2px solid ${COLORS_SOCIOS[nombre] || 'var(--border)'}`, borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: COLORS_SOCIOS[nombre] || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>{ini(nombre)}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Mi cuenta</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)' }}>Ganancias acumuladas</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={{ background: 'var(--surf2)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 3 }}>Acreditado</div>
            <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 18, color: 'var(--grn)' }}>{fmtM(parseFloat(socioData.saldo)||0)}</div>
          </div>
          <div style={{ background: 'var(--surf2)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 3 }}>Disponible</div>
            <div style={{ fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 18, color: disponible >= 0 ? 'var(--grn)' : 'var(--red)' }}>{fmtM(disponible)}</div>
          </div>
        </div>
        <button onClick={openRetiro} style={{ width: '100%', padding: 12, background: COLORS_SOCIOS[nombre] || 'var(--grn)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          − Registrar retiro
        </button>
      </div>

      {/* Simulador mejorado */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)', marginBottom: 14 }}>💊 ¿Cuánto puedo comprar esta semana?</div>

        {/* Datos clave siempre visibles */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          <div style={{ background: 'var(--grn-l)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--grn)', textTransform: 'uppercase', marginBottom: 3 }}>Máximo recomendado</div>
            <div style={{ fontFamily: 'Courier, monospace', fontWeight: 800, fontSize: 20, color: 'var(--grn)' }}>
              {fmtM(Math.max(0, flujo.totalProyectado - flujo.pagosEnPeriodo))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>para comprar esta semana</div>
          </div>
          <div style={{ background: 'var(--blu-l)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--blu)', textTransform: 'uppercase', marginBottom: 3 }}>Saldo estimado en caja</div>
            <div style={{ fontFamily: 'Courier, monospace', fontWeight: 800, fontSize: 20, color: 'var(--blu)' }}>
              {fmtM(flujo.totalProyectado)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>caja + ingresos proyectados</div>
          </div>
        </div>

        {/* Simulador */}
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', marginBottom: 6 }}>Simular una compra específica</div>
        <input type="number" value={simMonto} onChange={e => setSimMonto(e.target.value)}
          placeholder="Ingresá el monto..."
          style={{ width: '100%', padding: '11px 14px', borderRadius: 8, border: `1.5px solid ${simulacion ? (simulacion.viable ? 'var(--grn)' : 'var(--red)') : 'var(--border2)'}`, fontSize: 18, fontFamily: 'Courier, monospace', fontWeight: 700, background: 'var(--surf)', color: 'var(--tx)', outline: 'none', boxSizing: 'border-box', marginBottom: simulacion ? 8 : 0 }} />
        {simulacion && (
          <div style={{ background: simulacion.viable ? 'var(--grn-l)' : 'var(--red-l)', borderRadius: 8, padding: '12px 14px', border: `1px solid ${simulacion.viable ? 'var(--grn)' : 'var(--red)'}` }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: simulacion.viable ? 'var(--grn)' : 'var(--red)', marginBottom: 4 }}>
              {simulacion.viable ? '✓ VIABLE' : '✗ NO ALCANZA'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--tx2)' }}>
              Balance proyectado: <strong style={{ fontFamily: 'Courier', color: simulacion.viable ? 'var(--grn)' : 'var(--red)' }}>{fmtM(simulacion.balanceConCompra)}</strong>
            </div>
            {!simulacion.viable && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                Te faltan <strong>{fmtM(Math.abs(simulacion.balanceConCompra))}</strong> para cubrir esta compra
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cargar venta */}
      <button onClick={openVenta} style={{ width: '100%', padding: 16, background: 'var(--grn)', color: '#fff', border: 'none', borderRadius: 'var(--rl)', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
        + Cargar venta / cierre de caja
      </button>

      {/* Venta modal */}
      {ventaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setVentaModal(false); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', padding: 22, width: 420, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Cargar venta</h3>
              <button onClick={() => setVentaModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[['ef','Efectivo'],['tr','Transferencia'],['qr','QR / MP']].map(([k,l]) => (
                <div key={k}>
                  <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{l}</label>
                  <input type="number" value={ventaForm[k]||''} onChange={e => setVentaForm(f => ({...f,[k]:e.target.value}))}
                    placeholder="0"
                    style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 15, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            {totalVenta > 0 && (
              <div style={{ textAlign: 'center', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 20, color: 'var(--grn)', marginBottom: 10 }}>
                Total: {fmtM(totalVenta)}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Turno</label>
              <select value={ventaForm.turno} onChange={e => setVentaForm(f => ({...f,turno:e.target.value}))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)' }}>
                <option value="cierre_dia">Cierre del día</option>
                <option value="cierre_manana">Cierre mañana</option>
                <option value="cierre_tarde">Cierre tarde</option>
                <option value="venta_externa">Venta externa</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <button onClick={saveVenta} style={{ width: '100%', padding: 13, background: 'var(--grn)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Guardar venta
            </button>
          </div>
        </div>
      )}

      {/* Retiro modal */}
      {retiroModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
          onClick={e => { if (e.target === e.currentTarget) setRetiroModal(false); }}>
          <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', padding: 22, width: 380, boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Registrar retiro</h3>
              <button onClick={() => setRetiroModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Monto</label>
                <input type="number" value={retiroForm.monto} onChange={e => setRetiroForm(f => ({...f,monto:e.target.value}))}
                  autoFocus placeholder="0"
                  style={{ width: '100%', padding: '10px', borderRadius: 7, border: '1px solid var(--border2)', fontFamily: 'Courier, monospace', fontWeight: 700, fontSize: 20, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Fecha</label>
                <input type="date" value={retiroForm.fecha} onChange={e => setRetiroForm(f => ({...f,fecha:e.target.value}))}
                  style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Medio</label>
                <select value={retiroForm.medio} onChange={e => setRetiroForm(f => ({...f,medio:e.target.value}))}
                  style={{ width: '100%', padding: '8px', borderRadius: 7, border: '1px solid var(--border2)', fontSize: 13, background: 'var(--surf)', color: 'var(--tx)' }}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>
            <button onClick={saveRetiro} style={{ width: '100%', padding: 13, background: COLORS_SOCIOS[nombre] || 'var(--grn)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Confirmar retiro
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
